import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminGetStats, adminGetTrainingRegistrations, adminGetTournamentRegistrations, adminGetMessages } from '../../lib/api';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Users, Trophy, Calendar, Newspaper, Handshake, Image,
  CreditCard, Mail, LayoutDashboard, ChevronRight, Target,
  Bell, UserCheck, CalendarCheck, Trash2
} from 'lucide-react';

const AdminLayout = () => {
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTrainingRegs, setRecentTrainingRegs] = useState([]);
  const [recentTournamentRegs, setRecentTournamentRegs] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchAll = async () => {
      try {
        const [statsRes, trRes, toRes, msgRes] = await Promise.all([
          adminGetStats(),
          adminGetTrainingRegistrations(),
          adminGetTournamentRegistrations(),
          adminGetMessages(),
        ]);
        setStats(statsRes.data);
        setRecentTrainingRegs(trRes.data.slice(0, 5));
        setRecentTournamentRegs(toRes.data.slice(0, 5));
        setRecentMessages(msgRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching admin data:', error);
      }
    };
    fetchAll();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="admin-unauthorized">
        <Card className="card-elevated max-w-md">
          <CardContent className="p-8 text-center">
            <Target className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold uppercase mb-4">Zugriff verweigert</h1>
            <p className="text-text-secondary mb-6">
              Du hast keine Berechtigung für den Admin-Bereich.
            </p>
            <Link to="/"><Button className="btn-primary">Zur Startseite</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const unreadMessages = stats?.unread_messages || 0;

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Mitglieder', path: '/admin/members', icon: Users },
    { name: 'Mannschaften', path: '/admin/teams', icon: Target },
    { name: 'Turniere', path: '/admin/tournaments', icon: Trophy },
    { name: 'Training', path: '/admin/training', icon: Calendar },
    { name: 'Anwesenheit', path: '/admin/attendance', icon: CalendarCheck },
    { name: 'News', path: '/admin/news', icon: Newspaper },
    { name: 'Sponsoren', path: '/admin/sponsors', icon: Handshake },
    { name: 'Galerie', path: '/admin/gallery', icon: Image },
    { name: 'Nachrichten', path: '/admin/messages', icon: Mail, badge: unreadMessages },
  ];

  const isActive = (path) => location.pathname === path;

  // Dashboard view
  if (location.pathname === '/admin') {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="admin-dashboard">
        {/* Header */}
        <section className="section-padding bg-surface">
          <div className="container-max">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
                  Admin <span className="text-primary">Dashboard</span>
                </h1>
                <p className="text-text-secondary">
                  Willkommen, {user?.name}! Verwalte hier deinen Verein.
                </p>
              </div>
              {unreadMessages > 0 && (
                <Link to="/admin/messages">
                  <Card className="card-elevated border-primary/50 animate-pulse cursor-pointer" data-testid="new-message-notification">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Bell className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">{unreadMessages} neue Nachricht{unreadMessages > 1 ? 'en' : ''}</span>
                    </CardContent>
                  </Card>
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Stats Grid */}
        <section className="section-padding">
          <div className="container-max">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                <Card className="card-surface">
                  <CardContent className="p-6 text-center">
                    <Users className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-3xl font-heading font-bold">{stats.total_members}</p>
                    <p className="text-text-secondary text-sm">Mitglieder</p>
                  </CardContent>
                </Card>
                <Card className="card-surface">
                  <CardContent className="p-6 text-center">
                    <UserCheck className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-3xl font-heading font-bold text-accent">{stats.active_members}</p>
                    <p className="text-text-secondary text-sm">Aktive</p>
                  </CardContent>
                </Card>
                <Card className="card-surface">
                  <CardContent className="p-6 text-center">
                    <Trophy className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-3xl font-heading font-bold">{stats.total_tournaments}</p>
                    <p className="text-text-secondary text-sm">Turniere</p>
                  </CardContent>
                </Card>
                <Card className="card-surface">
                  <CardContent className="p-6 text-center">
                    <Mail className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-3xl font-heading font-bold text-accent">{stats.unread_messages || 0}</p>
                    <p className="text-text-secondary text-sm">Neue Nachrichten</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mb-8">
              <Button
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={async () => {
                  if (!window.confirm('Alle Benachrichtigungen für alle Benutzer löschen?')) return;
                  try {
                    const res = await api.delete('/admin/notifications');
                    const { toast } = await import('sonner');
                    toast.success(res.data.message);
                  } catch (e) {
                    const { toast } = await import('sonner');
                    toast.error('Fehler beim Löschen');
                  }
                }}
                data-testid="delete-all-notifications-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Alle Benachrichtigungen löschen
              </Button>
            </div>

            {/* Activity Feeds */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Training Registrations */}
              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                      <CalendarCheck className="w-4 h-4 text-primary" />
                      Training-Anmeldungen
                    </h3>
                    <Badge variant="outline" className="text-xs">{stats?.total_training_regs || 0}</Badge>
                  </div>
                  {recentTrainingRegs.length > 0 ? (
                    <div className="space-y-3">
                      {recentTrainingRegs.map((reg, i) => (
                        <div key={reg.registration_id || i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                          <div>
                            <p className="font-medium">{reg.user_name}</p>
                            <p className="text-text-secondary text-xs">
                              {reg.session_date} {reg.session_time && `um ${reg.session_time}`}
                            </p>
                          </div>
                          <span className="text-xs text-text-secondary">{formatDate(reg.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-secondary text-sm">Keine Anmeldungen</p>
                  )}
                  <Link to="/admin/training" className="text-xs text-accent hover:underline mt-3 inline-block">
                    Alle anzeigen
                  </Link>
                </CardContent>
              </Card>

              {/* Tournament Registrations */}
              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-primary" />
                      Turnier-Anmeldungen
                    </h3>
                    <Badge variant="outline" className="text-xs">{stats?.total_tournament_regs || 0}</Badge>
                  </div>
                  {recentTournamentRegs.length > 0 ? (
                    <div className="space-y-3">
                      {recentTournamentRegs.map((reg, i) => (
                        <div key={reg.registration_id || i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0">
                          <div>
                            <p className="font-medium">{reg.user_name}</p>
                            <p className="text-text-secondary text-xs">{reg.tournament_name}</p>
                          </div>
                          <span className="text-xs text-text-secondary">{formatDate(reg.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-secondary text-sm">Keine Anmeldungen</p>
                  )}
                  <Link to="/admin/tournaments" className="text-xs text-accent hover:underline mt-3 inline-block">
                    Alle anzeigen
                  </Link>
                </CardContent>
              </Card>

              {/* Recent Messages */}
              <Card className="card-surface">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary" />
                      Kontaktanfragen
                    </h3>
                    {unreadMessages > 0 && (
                      <Badge className="bg-primary text-white text-xs">{unreadMessages} Neu</Badge>
                    )}
                  </div>
                  {recentMessages.length > 0 ? (
                    <div className="space-y-3">
                      {recentMessages.map((msg, i) => (
                        <div key={msg.message_id || i} className={`flex items-center justify-between text-sm border-b border-white/5 pb-2 last:border-0 ${msg.status === 'new' ? 'text-white' : ''}`}>
                          <div>
                            <p className={`font-medium ${msg.status === 'new' ? '' : 'text-text-secondary'}`}>{msg.name}</p>
                            <p className="text-text-secondary text-xs truncate max-w-[180px]">{msg.subject}</p>
                          </div>
                          <span className="text-xs text-text-secondary">{formatDate(msg.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-text-secondary text-sm">Keine Nachrichten</p>
                  )}
                  <Link to="/admin/messages" className="text-xs text-accent hover:underline mt-3 inline-block">
                    Alle anzeigen
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Quick Links */}
            <h2 className="text-2xl font-heading font-bold uppercase mb-6">Verwaltung</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {navItems.slice(1).map((item) => (
                <Link key={item.path} to={item.path}>
                  <Card className="card-surface hover:border-primary/50 transition-all">
                    <CardContent className="p-6 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-primary" />
                        </div>
                        <span className="font-heading font-bold uppercase">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.badge > 0 && (
                          <Badge className="bg-primary text-white">{item.badge}</Badge>
                        )}
                        <ChevronRight className="w-5 h-5 text-text-secondary" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Sub-page layout
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="admin-layout">
      <div className="container-max px-4">
        <div className="flex flex-col lg:flex-row gap-8 py-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <Card className="card-surface sticky top-24">
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center justify-between px-4 py-3 rounded-sm transition-colors ${
                        isActive(item.path)
                          ? 'bg-primary text-white'
                          : 'text-text-secondary hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span className="text-sm font-medium">{item.name}</span>
                      </div>
                      {item.badge > 0 && (
                        <Badge className="bg-primary text-white text-xs h-5 w-5 flex items-center justify-center p-0 rounded-full">
                          {item.badge}
                        </Badge>
                      )}
                    </Link>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
